<template>
    <div>
        <v-row class="justify-space-between results-sort-row mt-3 mt-md-0">
            <v-col cols="12" md="auto" class="d-flex align-center results-found pt-0 pb-0 order-md-0 order-1">
                <p v-if="isLoadingProperties">
                    <v-progress-circular size="16" width="2" indeterminate class="mr-2" />
                    Loading...
                </p>
                <p v-else>{{ propertiesPagination.total }} Results Found</p>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" class="d-none d-md-flex align-center pt-0">
                <PropertySortByFilter/>
            </v-col>
        </v-row>
        <v-row class="properties-map-row">
            <v-col cols="12" order-md="2" md="6" v-if="display === 'map'" :class="['map-column', `map-column--${source}`]">
                <div class="gmap-container">
                    <div v-if="isLoadingMapMarkers" class="map-loading-overlay">
                        <v-progress-circular size="36" width="3" indeterminate color="primary" />
                        <span class="map-loading-text">Loading map...</span>
                    </div>
                    <GoogleMap
                        :markers="propertiesMarkers"
                        :highlighted-property-id="hoveredPropertyId"
                        height="100%"
                        @marker-hover="mapHoveredPropertyId = $event"
                    />
                </div>
            </v-col>
            <v-col cols="12" order-md="1" :md="display === 'map' ? 6 : 12">
                <v-row class="justify-start" :class="$vuetify.breakpoint.mdAndUp ? 'properties-container' : ''">
                    <!-- Skeleton loaders while loading -->
                    <template v-if="isLoadingProperties">
                        <v-col
                            v-for="n in 6"
                            :key="'skeleton-' + n"
                            cols="12"
                            :md="display === 'map' ? 12 : 6"
                            :lg="display === 'map' ? 6 : 4"
                            :xl="display === 'map' ? 6 : 3"
                        >
                            <v-card class="property-skeleton" height="340" max-width="450">
                                <!-- Image area -->
                                <v-skeleton-loader type="image" height="200" />
                                <!-- Content area -->
                                <v-card-text class="py-2">
                                    <v-row class="pb-1">
                                        <v-col cols="6" class="pb-0">
                                            <v-skeleton-loader type="heading" class="mb-2" />
                                            <v-skeleton-loader type="text" />
                                            <v-skeleton-loader type="text" width="80%" />
                                        </v-col>
                                        <v-col cols="6" class="text-right">
                                            <v-skeleton-loader type="text" width="70%" class="ml-auto mb-2" />
                                            <v-skeleton-loader type="text" width="50%" class="ml-auto" />
                                            <v-skeleton-loader type="text" width="50%" class="ml-auto" />
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </template>
                    <!-- Property cards when loaded -->
                    <template v-else v-for="(property, index) in properties">
                        <v-col
                            :key="index"
                            cols="12"
                            :md="display === 'map' ? 12 : 6"
                            :lg="display === 'map' ? 6 : 4"
                            :xl="display === 'map' ? 6 : 3"
                        >
                            <PropertyCard
                                :property="property"
                                :distance-to-school="getDistance(property)"
                                source="properties"
                                :highlighted="mapHoveredPropertyId === property.id"
                                @hover="hoveredPropertyId = $event"
                            />
                        </v-col>
                    </template>
                </v-row>
                <v-row class="justify-center py-4">
                    <Paginate
                        state="Properties"
                        collection="properties"
                        :list="paginationList"
                    />
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import GoogleMap from "../components/GoogleMap";
import Paginate from "../components/Paginate";
import PropertyCard from "../components/PropertyCard";
import PropertySortByFilter from "../components/PropertySortByFilter";
import {calculateGeoDistances} from "@/utils/utils"
import {mapActions, mapGetters} from "vuex";

export default {
    name: 'PropertiesView',
    props: {
        display: {
            type: String,
            default: 'map'
        },
        source: {
            type: String,
            default: 'properties'
        }
    },
    components: {
        Paginate,
        PropertyCard,
        GoogleMap,
        PropertySortByFilter
    },

    data() {
        return {
            markers: [{
                position: {
                    lat: 28.385233,
                    lng: -81.566068
                },
                infoText: ''
            }],
            hoveredPropertyId: null,
            mapHoveredPropertyId: null,
        }
    },
    metaInfo() {
        return {
            title: this.getH1Title() + ' Student Rentals',
        }
    },
    computed: {
        ...mapGetters('Campus', {
            campuses: 'campuses',
            getCampusById: 'getCampusById'
        }),
        isUserLoggedIn() {
            return this.$store.getters.isUserLoggedIn;
        },
        user() {
            return this.$store.getters.getUser;
        },
        userCampusId() {
            // If the user is logged in, use their campus id
            if (this.isUserLoggedIn && this.user) {
                return Number(this.user.campus_id);
            }

            // if we are searching for a specific school campus use that instead
            if (this.$route.params.type === 'campus') {
                return Number(this.$route.params.id);
            }

            // Otherwise return -1
            return -1;
        },
        userSchoolCampus() {
            return this.getCampusById(Number(this.userCampusId));
        },
        properties() {
            return this.$store.getters.getProperties
        },
        propertiesNoPagination() {
            return this.$store.getters.propertiesNoPagination
        },
        paginationList() {
            return this.isUserLoggedIn ? 'getProperties' : 'getPublicProperties';
        },
        propertiesPagination() {
            return this.$store.getters.getPropertiesPagination;
        },
        isLoadingProperties() {
            return this.$store.getters.getIsLoadingProperties;
        },
        isLoadingMapMarkers() {
            return this.$store.getters.getIsLoadingMapMarkers;
        },
        propertiesMarkers() {
            let markers = [];

            // Set the school marker for this user
            if (this.userSchoolCampus) {
                markers = [];
                markers.push({
                    position: {
                        lat: Number(this.userSchoolCampus.latitude),
                        lng: Number(this.userSchoolCampus.longitude)
                    },
                    icon: 'map-school.svg',
                    type: 'school',
                    school: this.userSchoolCampus
                });
            }
            if (this.propertiesNoPagination) {
                for (const unit of this.propertiesNoPagination) {
                    markers.push({
                        position: {
                            lat: Number(unit.latitude),
                            lng: Number(unit.longitude)
                        },
                        icon: 'map-building.svg',
                        type: 'propertyUnit',
                        propertyUnit: unit,
                    });
                }
            } else if (this.propertiesNoPagination && this.propertiesNoPagination.length === 0) {
                markers = [{
                    position: {
                        lat: 28.385233,
                        lng: -81.566068
                    },
                    infoText: ''
                }];
            }
            return markers;
        }
    },

    methods: {
        ...mapActions('Campus', {
            getCampuses: 'getCampuses'
        }),
        getDistance(property) {
            if (!this.userSchoolCampus) {
                return -1
            }

            return Number(calculateGeoDistances(
                this.userSchoolCampus.latitude,
                this.userSchoolCampus.longitude,
                property.latitude,
                property.longitude
            )).toFixed(0)
        },
        getH1Title() {
            return this.userSchoolCampus ? this.userSchoolCampus.name : this.$route.params.type === 'city' ? this.$route.params.id : '';
        },
    },
    created() {
        this.unsubscribe = this.$store.subscribe((mutation) => {
            if (mutation.type === 'setProperties') {
                this.$store.dispatch(this.isUserLoggedIn ? 'getPropertiesNoPagination' : 'getPublicPropertiesNoPagination');
            }
        });
    },
    beforeMount() {
        if (this.campuses.length === 0) {
            this.getCampuses()
        }
    },
    beforeDestroy() {
        this.unsubscribe();
    }
}
</script>

<style scoped>
    .results-sort-row {
        flex-wrap: wrap;
    }

    .properties-map-row {
        align-items: flex-start;
    }

    .map-column {
        position: sticky;
        z-index: 1;
    }

    .map-column--properties {
        top: 80px;
        height: calc(100vh - 100px);
    }

    .map-column--search {
        top: 200px;
        height: calc(100vh - 200px);
    }

    .gmap-container {
        position: relative;
        width: 100%;
        height: 100%;
        margin-left: 0px;
        margin-right: 0px;
    }

    .map-loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.75);
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
    }

    .map-loading-text {
        margin-top: 12px;
        font-size: 14px;
        font-weight: 500;
        color: #555;
    }

    .properties-container {
        height: auto;
        max-height: none;
        overflow-y: visible;
    }

    .search-properties-title {
      font-size: 18px !important;
      font-weight: 700 !important;
    }

    .property-skeleton {
        border-radius: 10px !important;
        overflow: hidden;
    }

    .property-skeleton .v-skeleton-loader__image {
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
    }

    .property-skeleton .v-skeleton-loader__heading {
        max-width: 90%;
    }

    @media only screen and (max-width: 960px) {
        .map-column {
            position: relative;
            top: 0;
            height: 70vh;
            min-height: 400px;
        }
        .gmap-container {
            height: 100%;
        }
        .mobile-hide {
            display: none !important;
        }
    }
</style>
